/**
 * Sesión del usuario: quién está dentro, entrar, salir y registrarse.
 *
 * La sesión vive en la cookie que pone Django, no en `localStorage`: un XSS
 * no puede leerla y el navegador la manda solo al mismo origen. Aquí no se
 * guarda ningún token; lo único que se cachea es la respuesta de
 * `/api/cuentas/yo/`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ErrorApi, api } from './cliente'
import type { DatosRegistro, Usuario } from './tipos'

export const claveSesion = ['sesion'] as const

/**
 * Quién está en sesión, o `null` si nadie.
 *
 * Un 403 aquí no es un fallo que haya que enseñar: es la respuesta normal
 * cuando no se ha entrado. Por eso se traduce a `null` en vez de dejar que
 * la consulta quede en error, y no se reintenta.
 */
export function useSesion() {
  return useQuery({
    queryKey: claveSesion,
    queryFn: async (): Promise<Usuario | null> => {
      try {
        return await api.get<Usuario>('/cuentas/yo/')
      } catch (error) {
        if (error instanceof ErrorApi && (error.status === 403 || error.status === 401)) {
          return null
        }
        throw error
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  })
}

export function useEntrar() {
  const cache = useQueryClient()
  return useMutation({
    mutationFn: (datos: { email: string; password: string }) =>
      api.post<Usuario>('/cuentas/entrar/', datos),
    onSuccess: (usuario) => {
      cache.setQueryData(claveSesion, usuario)
    },
  })
}

export function useSalir() {
  const cache = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<void>('/cuentas/salir/'),
    onSettled: () => {
      // Se limpia todo, no solo la sesión: la caché tiene las
      // investigaciones del usuario que se va, y el siguiente que entre en
      // este navegador no debe verlas ni un instante.
      cache.clear()
    },
  })
}

export function useRegistrarse() {
  return useMutation({
    mutationFn: (datos: DatosRegistro) =>
      api.post<{ detail: string; code: string }>('/cuentas/registro/', datos),
  })
}
